import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    IExperiment,
    getExperimentTypeIcon,
    formatDate
} from '../../types/experiment';

interface ExperimentCardProps {
    experiment: IExperiment;
}

const ExperimentCard: React.FC<ExperimentCardProps> = ({ experiment }) => {
    const { t } = useTranslation();

    const expData = experiment.experiment_data;
    const rawStatus = experiment.progress?.run_status || expData?.status || 'draft';
    const status = rawStatus.toLowerCase(); // Normalize to lowercase for translation keys
    const type = expData?.type || 'unknown';

    const getExperimentConfidence = () => {
        if (type?.toLowerCase() === 'classification' && experiment.overall_classification_confidence_avg !== undefined) {
            return experiment.overall_classification_confidence_avg;
        }
        return experiment.overall_average_confidence;
    };

    const confidence = getExperimentConfidence();
    const progress = experiment.progress;
    const isRunning = status === 'running';

    return (
        <Link to={`/experiments/${experiment._id}`} className="experiment-card">
            <div className="experiment-card-header">
                <span className={`status-badge ${status}`}>
                    {isRunning && <span className="status-spinner" />}
                    {t(`experiments.status.${status}`) || status}
                </span>
                <span className="experiment-type">
                    <span className="material-icons">{getExperimentTypeIcon(type)}</span>
                    {type}
                </span>
            </div>

            <h3 className="experiment-card-title">{experiment.experiment_id || t('experiments.unnamed')}</h3>

            <div className="experiment-card-meta">
                <div className="meta-item">
                    <span className="material-icons">smart_toy</span>
                    <span>
                        {expData?.provider && expData?.model
                            ? `${expData.provider} / ${expData.model}`
                            : expData?.provider || expData?.model || '-'}
                    </span>
                </div>
                <div className="meta-item">
                    <span className="material-icons">description</span>
                    <span>{expData?.number_of_processed_documents || 0} {t('experiments.documents')}</span>
                </div>
                <div className="meta-item">
                    <span className="material-icons">schedule</span>
                    <span>{formatDate(experiment.created_timestamp || experiment.experiment_created_time)}</span>
                </div>
                {experiment.last_used_timestamp && (
                    <div className="meta-item">
                        <span className="material-icons">update</span>
                        <span>{formatDate(experiment.last_used_timestamp)}</span>
                    </div>
                )}
                {(progress?.finished_at || progress?.started_at) && (
                    <div className="meta-item">
                        <span className="material-icons">play_circle</span>
                        <span>{formatDate(progress.finished_at || progress.started_at)}</span>
                    </div>
                )}
            </div>

            {/* Tags */}
            {expData?.tags && expData.tags.length > 0 && (
                <div className="experiment-card-tags">
                    {expData.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="tag-badge-small">{tag}</span>
                    ))}
                    {expData.tags.length > 3 && (
                        <span className="tag-badge-small more">+{expData.tags.length - 3}</span>
                    )}
                </div>
            )}

            {/* Progress bar for running experiments */}
            {isRunning && progress && progress.total_items > 0 && (
                <div className="experiment-card-progress">
                    <div className="progress-bar-small">
                        <div
                            className="progress-fill running"
                            style={{ width: `${(progress.processed_items / progress.total_items) * 100}%` }}
                        />
                    </div>
                    <span className="progress-text-small">
                        {progress.processed_items}/{progress.total_items}
                    </span>
                </div>
            )}

            {/* Metrics preview */}
            {confidence !== undefined && (
                <div className="experiment-card-metrics">
                    <span className="metric">
                        <span className="metric-label">{t('experiments.confidence')}</span>
                        <span className="metric-value">{(confidence * 100).toFixed(1)}%</span>
                    </span>
                </div>
            )}
        </Link>
    );
};

export default ExperimentCard;
